import React from 'react'
import { useCustomTranslation } from '@e3dc-react/shell/Hooks/useCustomTranslation'
import { Collapse } from '@mui/material'
import { useFormikContext } from 'formik'
import { SettingsBox } from 'src/Elements/Data'
import { NumberField } from 'src/Libs/inputs/formik'
import { UnitTypes } from 'src/Libs/types'
import { DynamicPricesModel, GeneralFormFields } from '../../../types'
import { SingleToggleSettingBox } from 'src/Elements/SystemSettings/forms'
import { useFetchDynamicTariffSettings } from 'src/Libs/dynamic-tariff-settings/hooks'
import { checkIsEnergyCostDynamic } from 'src/Libs/dynamic-power-prices/utils'
import { PriceBasedChargingAlert } from '../../alert'
import { useInstallationExtensionData } from 'src/Libs/installation-extensions'

interface Props {
  installationId?: string
  hasWritingRights?: boolean
  isDppWallboxToggleDisabled: boolean
}

/**
 * This component renders the minimal version of dynamic prices form fields (used for older target versions)
 */
const MinimalVersionFormContent: React.FC<Props> = ({ installationId, isDppWallboxToggleDisabled, hasWritingRights = true }) => {
  const { values } = useFormikContext<DynamicPricesModel>()
  const { t } = useCustomTranslation()

  const { dynamicTariffSettings } = useFetchDynamicTariffSettings(installationId)
  const { isActiveExtensionAvailable } = useInstallationExtensionData(installationId)

  /* The price based charging is only available if the energy cost is dynamic */
  const isEnergyCostDynamic = checkIsEnergyCostDynamic(dynamicTariffSettings)
  const canUserChangeSettings = hasWritingRights && (isEnergyCostDynamic || isActiveExtensionAvailable)
  const showPriceBasedChargingAlert = !isEnergyCostDynamic && !isActiveExtensionAvailable

  return (
    <>
      {showPriceBasedChargingAlert && (
        <PriceBasedChargingAlert text={t('systemSettings.price-based-charging.alert.electricity-tariff-not-dynamic')} />
      )}

      <SingleToggleSettingBox
        title={t('systemSettings.dynamic-prices.dynamic-electricity-tariffs.title')}
        name="dynamicPricesForWallboxesEnabled"
        disabled={!hasWritingRights || isDppWallboxToggleDisabled || !isEnergyCostDynamic}
        description={t('systemSettings.dynamic-prices.dynamic-electricity-tariffs.description')}
        tooltipText={isDppWallboxToggleDisabled ? t('systemSettings-dynamic-prices.wallbox.dpp-not-available') : ''}
      />

      <Collapse in={values.dynamicPricesForWallboxesEnabled}>
        {extraFields.map(({ titleKey, descriptionKey, fieldName, fieldSuffix, isDecimal, step, precision }) => (
          <SettingsBox
            key={titleKey}
            title={t(titleKey)}
            description={t(descriptionKey)}
            disabled={!canUserChangeSettings}
            suffix={
              <NumberField
                name={fieldName}
                suffix={fieldSuffix}
                isDecimal={isDecimal}
                step={step}
                precision={precision}
                ignoreNonNumerics
                disabled={!canUserChangeSettings}
              />
            }
          />
        ))}
      </Collapse>
    </>
  )
}

export default MinimalVersionFormContent

const extraFields: GeneralFormFields[] = [
  {
    titleKey: 'systemSettings.dynamic-prices.price-limit-for-ev-charging.title',
    descriptionKey: 'systemSettings.dynamic-prices.price-limit-for-ev-charging.description',
    fieldName: 'priceLimitWallbox',
    fieldSuffix: UnitTypes.cents,
    isDecimal: true,
    step: 0.001,
    precision: 3,
  },
]
